<template>
    <div class="phone-formatter-input">
        <label v-if="label" for="phoneNumber">{{ label }}</label>
        <small v-if="description">{{ description }}</small>
        <div class="phone-input-container">
            <div class="country-selector" :class="{ active: showCountryDropdown }" @click="toggleCountryDropdown">
                <img :src="getFlagUrl(selectedCountry.code)" :alt="selectedCountry.name" class="flag-icon" />
                <span class="dial-code">{{ selectedCountry.dial_code }}</span>
                <span class="dropdown-arrow">▼</span>
            </div>
            <div class="phone-input-wrapper">
                <input
                    type="text"
                    ref="phoneInput"
                    :value="formattedPhoneNumber"
                    @input="onPhoneInput"
                    @keydown="onPhoneKeydown"
                    @paste="onPhonePaste"
                    :placeholder="getPhonePlaceholder()"
                    class="phone-input"
                />
                <span
                    class="info-icon md-icon"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                    }"
                    :content="infoTooltip"
                >
                    <span class="md-icon">info</span>
                </span>
            </div>

            <!-- Country Dropdown -->
            <div v-if="showCountryDropdown" class="country-dropdown">
                <div class="country-search">
                    <input
                        type="text"
                        v-model="countrySearchQuery"
                        placeholder="Ülke ara..."
                        @input="filterCountries"
                        ref="countrySearch"
                    />
                </div>
                <div class="country-list">
                    <div
                        v-for="country in filteredCountries"
                        :key="country.code"
                        class="country-item"
                        :class="{ selected: selectedCountry.code === country.code }"
                        @click="selectCountry(country)"
                    >
                        <img :src="getFlagUrl(country.code)" :alt="country.name" class="flag-icon" />
                        <span class="country-name">{{ getCountryDisplayName(country) }}</span>
                        <span class="dial-code">{{ country.dial_code }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import countries from "./countries";

export default Vue.extend({
    name: "PhoneFormaterinput",
    props: {
        value: {
            type: String,
            default: "",
        },
        label: {
            type: String,
            default: "Telefon Numarası",
        },
        description: {
            type: String,
            default: "Ülke kodunu seçin ve telefon numaranızı girin",
        },
        infoTooltip: {
            type: String,
            default: "Örnek: 555 123 45 67",
        },
    },
    data() {
        return {
            phoneNumber: "",
            selectedCountry: countries.find((c) => c.code === "TR") || countries[0],
            showCountryDropdown: false,
            countrySearchQuery: "",
            filteredCountries: countries,
        };
    },
    computed: {
        formattedPhoneNumber() {
            return this.formatPhoneNumber(this.phoneNumber);
        },
        fullPhoneNumber() {
            return this.getNormalizedPhoneNumber();
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (newValue && newValue !== this.fullPhoneNumber) {
                    this.parsePhoneNumber(newValue);
                }
            },
        },
        fullPhoneNumber(newValue) {
            this.$emit("input", newValue);
            this.$emit("change", {
                fullNumber: newValue,
                phoneNumber: this.phoneNumber,
                countryCode: this.selectedCountry.code,
                dialCode: this.selectedCountry.dial_code,
            });
        },
    },
    methods: {
        getFlagUrl(countryCode) {
            return `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;
        },

        getCountryDisplayName(country) {
            const query = (this.countrySearchQuery || "").toLowerCase().trim();
            if (query) {
                const turkishName = (country.turkish_name || "").toLowerCase();
                const englishName = (country.name || "").toLowerCase();
                const matchesTurkish = turkishName.includes(query) || turkishName.startsWith(query);
                const matchesEnglish = englishName.includes(query) || englishName.startsWith(query);
                if (matchesTurkish && !matchesEnglish) return country.turkish_name || country.name;
                if (matchesEnglish && !matchesTurkish) return country.name;
                if (matchesTurkish && matchesEnglish) return country.turkish_name || country.name;
            }
            return country.turkish_name || country.name;
        },

        toggleCountryDropdown() {
            this.showCountryDropdown = !this.showCountryDropdown;
            if (this.showCountryDropdown) {
                this.$nextTick(() => {
                    this.positionDropdown();
                    if (this.$refs.countrySearch) {
                        this.$refs.countrySearch.focus();
                    }
                });
            }
        },

        positionDropdown() {
            const container = this.$el.querySelector(".phone-input-container");
            const dropdown = this.$el.querySelector(".country-dropdown");
            if (container && dropdown) {
                dropdown.style.width = `${container.offsetWidth}px`;
            }
        },

        selectCountry(country) {
            this.selectedCountry = country;
            this.showCountryDropdown = false;
            this.countrySearchQuery = "";
            this.filteredCountries = countries;
        },

        filterCountries() {
            const query = this.countrySearchQuery.toLowerCase().trim();
            if (!query) {
                this.filteredCountries = countries;
                return;
            }

            this.filteredCountries = countries.filter((country) => {
                const turkishName = (country.turkish_name || "").toLowerCase();
                const englishName = country.name.toLowerCase();
                const dialCode = country.dial_code.toLowerCase();
                const countryCode = country.code.toLowerCase();

                const matchesTurkish = turkishName.includes(query) || turkishName.startsWith(query);
                const matchesEnglish = englishName.includes(query) || englishName.startsWith(query);
                const matchesDialCode = dialCode.includes(query) || dialCode.replace("+", "").includes(query);
                const matchesCode = countryCode.includes(query) || countryCode.startsWith(query);

                return matchesTurkish || matchesEnglish || matchesDialCode || matchesCode;
            });
        },

        formatPhoneNumber(number) {
            if (!number) return "";

            const cleaned = number.replace(/\D/g, "");
            const countryCode = this.selectedCountry.code;

            if (countryCode === "TR") {
                if (cleaned.length <= 3) return cleaned;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
                if (cleaned.length <= 8) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
                return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
            }

            if (countryCode === "DK") {
                if (cleaned.length <= 2) return cleaned;
                if (cleaned.length <= 4) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`;
                return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
            }

            if (countryCode === "XK") {
                if (cleaned.length <= 2) return cleaned;
                if (cleaned.length <= 5) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
                return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)}`;
            }

            if (cleaned.length <= 3) return cleaned;
            if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
            if (cleaned.length <= 10) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
            return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)} ${cleaned.slice(10)}`;
        },

        getPhonePlaceholder() {
            const countryCode = this.selectedCountry.code;
            if (countryCode === "TR") return "555 123 45 67";
            if (countryCode === "DK") return "12 34 56 78";
            if (countryCode === "XK") return "44 123 456";
            if (countryCode === "US" || countryCode === "CA") return "555 123 4567";
            if (countryCode === "GB") return "7911 123456";
            if (countryCode === "DE") return "30 12345678";
            if (countryCode === "FR") return "1 23 45 67 89";
            return "123 456 7890";
        },

        onPhoneInput(event) {
            const value = event.target.value;
            const cleaned = value.replace(/\D/g, "");
            this.phoneNumber = cleaned;

            this.$nextTick(() => {
                if (event.target.value !== this.formattedPhoneNumber) {
                    event.target.value = this.formattedPhoneNumber;
                }
            });
        },

        onPhoneKeydown(event) {
            const allowedKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Escape",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Home",
                "End",
            ];

            const isNumber = event.key >= "0" && event.key <= "9";
            const isAllowedKey = allowedKeys.includes(event.key);
            const keyLower = (event.key || "").toLowerCase();
            const isCtrlA = event.ctrlKey && keyLower === "a";
            const isCtrlC = event.ctrlKey && keyLower === "c";
            const isCtrlV = event.ctrlKey && keyLower === "v";
            const isCtrlX = event.ctrlKey && keyLower === "x";
            const isMetaA = event.metaKey && keyLower === "a";
            const isMetaC = event.metaKey && keyLower === "c";
            const isMetaV = event.metaKey && keyLower === "v";
            const isMetaX = event.metaKey && keyLower === "x";

            if (
                !isNumber &&
                !isAllowedKey &&
                !isCtrlA &&
                !isCtrlC &&
                !isCtrlV &&
                !isCtrlX &&
                !isMetaA &&
                !isMetaC &&
                !isMetaV &&
                !isMetaX
            ) {
                event.preventDefault();
            }
        },

        onPhonePaste(event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData).getData("text");
            const cleaned = pastedText.replace(/\D/g, "");
            if (cleaned) {
                this.parsePhoneNumber(pastedText);
                this.$nextTick(() => {
                    if (this.$refs.phoneInput) {
                        this.$refs.phoneInput.value = this.formattedPhoneNumber;
                        try {
                            const len = this.$refs.phoneInput.value.length;
                            this.$refs.phoneInput.setSelectionRange(len, len);
                        } catch (e) {}
                    }
                });
            }
        },

        parsePhoneNumber(text) {
            const rawText = String(text || "");
            const cleaned = rawText.replace(/\D/g, "");
            const trimmed = rawText.trim();
            const hasExplicitPrefix = trimmed.charAt(0) === "+" || trimmed.indexOf("00") === 0;
            const trCountry = countries.find(function (c) {
                return c.code === "TR";
            });
            const setTurkeyNumber = (localNumber) => {
                if (trCountry) {
                    this.selectedCountry = trCountry;
                }
                this.phoneNumber = localNumber;
            };

            if (!hasExplicitPrefix) {
                if (cleaned.length === 10) {
                    setTurkeyNumber(cleaned);
                    return;
                }

                if (cleaned.length === 11) {
                    if (cleaned.charAt(0) === "0") {
                        setTurkeyNumber(cleaned.substring(1));
                        return;
                    }

                    if (cleaned.charAt(0) === "5") {
                        setTurkeyNumber(cleaned.slice(cleaned.length - 10));
                        return;
                    }
                }
            }

            let bestMatch = null;
            let bestDial = "";
            for (const c of countries) {
                const dial = String(c.dial_code || "").replace(/\D/g, "");
                if (!dial) continue;
                if (cleaned.startsWith(dial) && dial.length > bestDial.length) {
                    bestMatch = c;
                    bestDial = dial;
                }
            }

            if (bestMatch && bestDial) {
                const remainingNumber = cleaned.substring(bestDial.length);

                if (bestMatch.code === "DK") {
                    this.selectedCountry = bestMatch;
                    this.phoneNumber = remainingNumber;
                    return;
                }

                if (bestMatch.code === "XK") {
                    this.selectedCountry = bestMatch;
                    this.phoneNumber = remainingNumber;
                    return;
                }

                if (remainingNumber.length >= 4) {
                    this.selectedCountry = bestMatch;
                    this.phoneNumber = remainingNumber;
                    return;
                }
            }

            if (cleaned.length === 8) {
                if (this.selectedCountry && this.selectedCountry.code === "DK") {
                    this.phoneNumber = cleaned;
                    return;
                }
                if (this.selectedCountry && this.selectedCountry.code === "XK") {
                    this.phoneNumber = cleaned;
                    return;
                }
            }

            this.phoneNumber = cleaned;
        },

        getNormalizedPhoneNumber() {
            const cleaned = (this.phoneNumber || "").replace(/\D/g, "");
            const dialCode = String((this.selectedCountry && this.selectedCountry.dial_code) || "").replace(/\D/g, "");
            return dialCode + cleaned;
        },

        handleClickOutside(event) {
            if (!this.showCountryDropdown) return;

            const dropdown = this.$el.querySelector(".country-dropdown");
            const selector = this.$el.querySelector(".country-selector");

            if (dropdown && selector && !dropdown.contains(event.target) && !selector.contains(event.target)) {
                this.showCountryDropdown = false;
            }
        },

        handleEscapeKey(event) {
            if (event.key === "Escape" && this.showCountryDropdown) {
                this.showCountryDropdown = false;
                event.stopPropagation();
            }
        },
    },
    created() {
        document.addEventListener("click", this.handleClickOutside);
        document.addEventListener("keydown", this.handleEscapeKey);
        window.addEventListener("resize", this.positionDropdown);
        window.addEventListener("scroll", this.positionDropdown);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        document.removeEventListener("keydown", this.handleEscapeKey);
        window.removeEventListener("resize", this.positionDropdown);
        window.removeEventListener("scroll", this.positionDropdown);
    },
});
</script>

<style scoped lang="scss">
.phone-formatter-input {
    margin-bottom: 20px;

    label {
        font-size: 14px;
        margin-bottom: 5px;
        display: block;
    }

    small {
        font-size: 12px;
        color: #999;
        margin-bottom: 5px;
        display: block;
    }
}

.phone-input-container {
    position: relative;
    display: flex;
    align-items: stretch;
    border: 1px solid #ddd;
    border-radius: 5px;
    overflow: visible;
    background: white;

    .country-selector {
        display: flex;
        align-items: center;
        padding: 8px 10px;
        background-color: #f8f9fa;
        border-right: 1px solid #ddd;
        cursor: pointer;
        min-width: 90px;
        gap: 6px;
        transition: background-color 0.2s;
        margin-bottom: 0;

        &:hover {
            background-color: #e9ecef;
        }

        .flag-icon {
            width: 24px;
            height: 18px;
            object-fit: cover;
            border-radius: 2px;
            flex-shrink: 0;
        }

        .dial-code {
            font-size: 14px;
            font-weight: 500;
            color: #495057;
            white-space: nowrap;
        }

        .dropdown-arrow {
            font-size: 10px;
            color: #6c757d;
            margin-left: 4px;
            transition: transform 0.2s;
        }

        &.active .dropdown-arrow {
            transform: rotate(180deg);
        }
    }

    .phone-input-wrapper {
        display: flex;
        align-items: center;
        flex: 1;
        position: relative;
        margin-bottom: 0;

        .phone-input {
            flex: 1;
            padding: 8px 40px 8px 10px;
            border: none;
            outline: none;
            font-size: 14px;
            background: transparent;
            height: 100%;

            &::placeholder {
                color: #6c757d;
            }

            &:focus {
                outline: none;
            }
        }

        .info-icon {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            cursor: pointer;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            color: #6c757d;
            transition: color 0.2s;
            flex-shrink: 0;

            &:hover {
                color: #495057;
                background-color: rgba(0, 0, 0, 0.05);
            }
        }
    }

    &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    .country-dropdown {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 5px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        z-index: 9999;
        max-height: 300px;
        overflow: hidden;

        .country-search {
            padding: 10px;
            border-bottom: 1px solid #eee;

            input {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 14px;

                &:focus {
                    outline: none;
                    border-color: #007bff;
                }
            }
        }

        .country-list {
            max-height: 250px;
            overflow-y: auto;

            .country-item {
                display: flex;
                align-items: center;
                padding: 10px 12px;
                cursor: pointer;
                gap: 10px;
                transition: background-color 0.2s;

                &:hover {
                    background-color: #f8f9fa;
                }

                &.selected {
                    background-color: #e3f2fd;
                }

                .flag-icon {
                    width: 24px;
                    height: 18px;
                    object-fit: cover;
                    border-radius: 2px;
                }

                .country-name {
                    flex: 1;
                    font-size: 14px;
                    color: #495057;
                }

                .dial-code {
                    font-size: 14px;
                    font-weight: 500;
                    color: #6c757d;
                }
            }
        }
    }
}
</style>
